/* El RTO de una unidad, dicho de una sola manera.
 *
 * ── POR QUÉ SALIÓ DE ADENTRO DE `RTO.jsx` ────────────────────────────────
 *
 * Vivía ahí, y estaba bien mientras el RTO se miraba en un solo lado. Con la
 * ficha de la unidad son dos pantallas que muestran el mismo veredicto, y
 * copiado serían **dos definiciones de cuándo un camión puede salir**: el día
 * que se mueva el corte de 30 días se mueve en una y la otra sigue contestando
 * distinto sobre el mismo camión, sin fallar en ningún lado.
 *
 * Es exactamente la razón por la que `Ubicacion` y `ChipUsuario` salieron de
 * adentro de sus pantallas, y la misma por la que `DIAS_AVISO` vive en
 * `shared/rto` y no en cada tabla.
 *
 * ── DOS CONTEXTOS, UN COMPONENTE ─────────────────────────────────────────
 *
 *   · en `/rto` es EL veredicto: hay un chofer esperando en el portón y esto
 *     es lo único que se lee;
 *   · en la ficha es UN bloque entre cinco, y la pregunta es más ancha.
 *
 * Lo que cambia es lo que tiene alrededor, no lo que dice. Por eso el
 * historial es opcional (`conHistorial`): en el portón sirve para ver quién
 * renueva sobre la hora, y en la ficha compite con los otros cuatro bloques.
 */

import { laManda, origen as origenDeLaFecha } from "../../shared/rtoDeclarada";
import DeclararRTO from "./DeclararRTO";

const VEREDICTO = {
  vigente: {
    titulo: "Puede salir",
    tono: "ok",
    detalle: (e) => `RTO al día. Vence en ${e.dias} días.`,
  },
  /* ES SU PROPIO VEREDICTO Y NO «Puede salir» A SECAS.
   *
   * Deja salir al camión igual —por eso el tono es `ok` y no `alerta`: un 0 km
   * con el título en regla no es un problema, y pintarlo de amarillo mandaría
   * a resolver algo que está bien—. Pero de dónde salió la fecha va DICHO: es
   * un papel que leyó una persona y no una revisión del organismo, y el día
   * que esté mal tipeada hay a quién preguntarle. Misma decisión que la
   * prórroga de 60 días del registro. */
  vigente_declarada: {
    titulo: "Puede salir",
    tono: "ok",
    detalle: (e) =>
      `Vence en ${e.dias} días, según la fecha del título. El CENT todavía no tiene ninguna revisión de esta patente.`,
  },
  por_vencer: {
    titulo: "Tiene que renovarlo antes de salir",
    tono: "alerta",
    // La regla que puso portería: con menos de 30 días no se espera al
    // vencimiento, se renueva antes de que el camión salga a la ruta.
    detalle: (e) =>
      `Le quedan ${e.dias} ${e.dias === 1 ? "día" : "días"}. Con menos de 30 hay que renovarlo antes de salir.`,
  },
  vencida: {
    titulo: "No puede salir",
    tono: "error",
    detalle: (e) =>
      `RTO vencido hace ${Math.abs(e.dias)} ${Math.abs(e.dias) === 1 ? "día" : "días"}.`,
  },
  sin_rto: {
    titulo: "No tiene RTO registrada",
    tono: "error",
    // Distinto de "vencida" a propósito: puede ser una unidad nueva que
    // todavía no la rindió. El camino para resolverlo no es el mismo — y
    // desde que un 0 km puede declarar la fecha del título, ese camino está
    // acá abajo en vez de ser una explicación sin salida.
    detalle: () =>
      "El CENT no tiene ninguna revisión para esta patente. Puede ser una unidad 0 km —no rinde por un año— o estar en otro régimen.",
  },
  sin_consultar: {
    titulo: "Todavía no se consultó",
    tono: "alerta",
    // Nunca decir "no tiene" cuando lo que pasa es que no se miró. Un camión
    // marcado sin RTO por un error de red es la forma más rápida de que nadie
    // le crea más a esta pantalla.
    detalle: () =>
      "Esta patente no entró todavía en la sincronización de la noche.",
  },
  sin_patente: {
    titulo: "No tiene patente cargada",
    tono: "alerta",
    // Distinto de "todavía no se consultó", y esa diferencia es la que importa:
    // aquél se resuelve esperando a la sincronización de la noche, éste no se
    // resuelve nunca hasta que alguien cargue el dato. Prometer una espera que
    // no lleva a ningún lado es peor que no decir nada.
    detalle: () =>
      "En el padrón esta unidad tiene el interno en el campo de la patente. Sin patente no hay RTO que consultar: hay que cargarla en CantApp.",
  },
};

export const fechaCorta = (f) =>
  f ? new Date(f + "T12:00:00").toLocaleDateString("es-AR") : "—";

/* `children` se dibuja ENTRE el turno y el historial, y esa posición es la
 * decisión: en el portón, la última ubicación va debajo del veredicto —dónde
 * estuvo es contexto, y arriba competiría con lo único que hay que leer con el
 * chofer esperando— pero antes del historial, que es lo más lejano de todo.
 * Sin la ranura, cada pantalla dibujaría lo suyo abajo de todo y el orden que
 * ya estaba pensado se perdería en la primera. */
const VeredictoRTO = ({
  estado,
  turno,
  historial,
  onAgenda,
  conHistorial = true,
  /* La fecha del título: se dibuja acá adentro y no como `children` porque es
   * parte del veredicto —es de dónde salió la fecha que se está leyendo—, no
   * algo que la pantalla de al lado le cuelga abajo. `DeclararRTO` se calla
   * solo cuando no hay nada declarado y no hay permiso para cargar. */
  dominio,
  puedeCargar = false,
  onDeclarar,
  onBorrarDeclarada,
  children,
}) => {
  const v = VEREDICTO[estado?.estado] || VEREDICTO.sin_consultar;

  return (
    <>
      {/* El veredicto grande y arriba. En el portón nadie va a interpretar una
          tabla de fechas con el chofer esperando. */}
      <div className={"rto-veredicto " + v.tono}>
        <strong>{v.titulo}</strong>
        <span>{v.detalle(estado || {})}</span>
      </div>

      {estado?.vence && (
        <div className="msg-datos">
          <div>
            <span className="et">Vence</span>
            {/* DE DÓNDE SALIÓ LA FECHA, pegado a la fecha. Sin esto, «vence el
                12/03» del CENT y «vence el 12/03» que alguien tipeó del título
                se leen igual, y no pesan lo mismo. */}
            <span>
              {fechaCorta(estado.vence)}
              {origenDeLaFecha(estado) && (
                <em className="tk-item-meta"> · {origenDeLaFecha(estado)}</em>
              )}
            </span>
          </div>
          {/* REVISADO, RESULTADO Y CERTIFICADO SON HECHOS DEL CENT, así que
              sólo se dibujan cuando la fecha salió de ahí.

              Con una fecha declarada los tres vienen vacíos —el camión nunca
              fue— y `Resultado` se pintaría con `rto-ojo`, o sea como un RTO
              Condicional o Rechazado: un 0 km en regla dibujado como un
              problema. «No fue todavía» y «fue y no aprobó» no se pueden ver
              igual. */}
          {laManda(estado) && (
            <>
              <div>
                <span className="et">Revisado</span>
                <span>{fechaCorta(estado.revisado)}</span>
              </div>
              <div>
                <span className="et">Resultado</span>
                {/* Un RTO vigente pero Condicional o Rechazado no es lo mismo
                    que uno Apto, y eso se pierde si sólo se muestra la
                    fecha. */}
                <span className={estado.resultado === "Apto" ? "" : "rto-ojo"}>
                  {estado.resultado || "—"}
                </span>
              </div>
              {estado.certificado && (
                <div>
                  <span className="et">Certificado</span>
                  <span>{estado.certificado}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* La fecha del título: la del 0 km que el CENT todavía no conoce, y la
          discrepancia cuando el CENT apareció con otra. */}
      <DeclararRTO
        estado={estado}
        dominio={dominio}
        puedeCargar={puedeCargar}
        onGuardar={onDeclarar}
        onBorrar={onBorrarDeclarada}
      />

      {/* EL TURNO ES UNA INTENCIÓN Y EL VENCIMIENTO ES UN HECHO, y por eso va
          en su propio bloque y con su propia palabra. Metidos en la misma
          línea, «le sacamos turno» se lee como «ya tiene el RTO» y el portón
          deja salir un camión vencido — está escrito en `AgendaRTO` desde que
          se construyó y sigue valiendo.

          Se MUESTRA y no se carga desde acá: la agenda es el único lugar donde
          se anota. Dos formas de cargar lo mismo obligan a adivinar en cuál, y
          el día que se acierte la que no es, ese turno no aparece en ningún
          lado.

          Sólo salen los turnos ABIERTOS. Uno ya hecho es historia, y acá al
          lado del vencimiento se leería como que la revisión está resuelta. */}
      <div className="msg-datos">
        <div>
          <span className="et">Turno de renovación</span>
          {turno ? (
            <span
              title={[
                turno.estado === "confirmado"
                  ? "Turno confirmado"
                  : "Turno pedido, sin confirmar",
                turno.planta || "",
                turno.obs || "",
              ]
                .filter(Boolean)
                .join(" · ")}
            >
              {fechaCorta(turno.fecha)}
              {turno.hora ? ` ${String(turno.hora).slice(0, 5)}` : ""}
              {turno.planta ? ` · ${turno.planta}` : ""}
              {turno.estado === "pedido" && (
                <em className="tk-item-meta"> · sin confirmar</em>
              )}
            </span>
          ) : (
            /* «No tiene turno» y «no lo miré» no se dicen igual: `null` es que
               no hay ninguno abierto, que es el caso de casi toda la flota. */
            <span className="tenue">
              Sin turno ·{" "}
              <button type="button" className="rto-link" onClick={onAgenda}>
                sacarlo en la agenda
              </button>
            </span>
          )}
        </div>
      </div>

      {children}

      {conHistorial && historial?.length > 1 && (
        <>
          <div className="hub-grupo">Revisiones anteriores</div>
          <div className="rto-historial">
            {historial.slice(1).map((h) => (
              <div key={h.vence}>
                <span>
                  {fechaCorta(h.revisado)} → {fechaCorta(h.vence)}
                </span>
                <span className={h.resultado === "Apto" ? "" : "rto-ojo"}>
                  {h.resultado}
                </span>
                <span className="tk-item-meta">{h.centro}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default VeredictoRTO;
